/*
 * Location page URL contract shared by the property and agent location pages.
 * Paths such as `/properties-by-location/ab/calgary`, `/en/agents-by-location/ab`, or
 * `/fr/proprietes-par-emplacement/qc/Montr%C3%A9al/` are parsed the same way for every
 * language: optional language segment, page segment, then `province[/city]`.
 * Location segments use one ASCII slug rule (possessive `'s` becomes `s`, accents removed,
 * lowercase, other runs become `-`) from api/site-urls.cjs, which the sitemap job also uses; canonical
 * URLs therefore never need percent-encoding and location text derived from them is safe to display.
 */
import siteUrls from '../api/site-urls.cjs';

export type LocationKind = 'properties' | 'agents';
export type LocationLanguage = 'en' | 'fr';

export interface LocationPage {
  kind: LocationKind;
  language: LocationLanguage;
  province: string;
  city: string;
}

export const PROVINCES: string[] = siteUrls.PROVINCES;

const PAGE_NAMES: Record<LocationKind, Record<LocationLanguage, string>> = siteUrls.PAGE_NAMES;

// Property city pages need this many listings in their map area to be indexable. The sitemap job
// uses a higher bar (20 by city field) so submitted URLs stay indexable.
export const MIN_PROPERTY_LISTINGS = 5;

// Administrative area names (rural municipalities, wards, numbered districts) are not places people search for.
export function isAdministrativeLocation(slug: string): boolean {
  return siteUrls.isAdministrativeLocation(slug);
}

export function locationSlug(value: string): string {
  return siteUrls.locationSlug(value);
}

export function getLocationPage(pathname: string): LocationPage | null {
  let segments: string[];
  try {
    segments = pathname.split('/').filter(Boolean).map(segment => decodeURIComponent(segment));
  } catch {
    return null;
  }
  const first = (segments[0] || '').toLowerCase();
  const hasLanguage = first === 'en' || first === 'fr';
  const language: LocationLanguage = first === 'fr' ? 'fr' : 'en';
  const pageName = (segments[hasLanguage ? 1 : 0] || '').toLowerCase();
  const kind = (Object.keys(PAGE_NAMES) as LocationKind[])
    .find(key => PAGE_NAMES[key][language] === pageName);
  if (!kind) { return null; }
  const rest = segments.slice(hasLanguage ? 2 : 1).map(locationSlug).filter(Boolean);
  if (rest.length > 2) { return { kind, language, province: '', city: '' }; }
  return { kind, language, province: rest[0] || '', city: rest[1] || '' };
}

export function canonicalLocationPath(kind: LocationKind, language: LocationLanguage, province: string, city = ''): string {
  return siteUrls.canonicalLocationPath(kind, language, province, city);
}

// Dashed form (`red-deer, ab`): the pages strip whitespace, then turn `-` into spaces.
export function getLocationKeyword(pathname: string): string {
  const page = getLocationPage(pathname);
  if (!page || !page.province) { return ''; }
  return page.city ? `${page.city}, ${page.province}` : page.province;
}
